import { OidcUserStatus, useOidc, useOidcUser } from '@axa-fr/react-oidc';
import { useEffect, useMemo, useRef } from 'react';
import {
  CommonUser,
  loginError,
  loginStart,
  loginSuccess,
  setHasRequestedAccess,
  useAppDispatch,
} from '@energy-stacks/store';

const LOGIN_REDIRECT_DELAY = 1000;

export const useLogin = <R, T extends CommonUser>(
  userNormalizer: (dto: R) => T
) => {
  const userInfo = useOidcUser();
  const userDto = userInfo.oidcUser as R | null;
  const user = useMemo(
    () => (userDto ? userNormalizer(userDto) : null),
    [userDto, userNormalizer]
  );
  const loadingState = userInfo.oidcUserLoadingState;
  const dispatch = useAppDispatch();
  const { isAuthenticated, login } = useOidc();
  const hasRequestedLogin = useRef(false);

  useEffect(() => {
    if (isAuthenticated || hasRequestedLogin.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      hasRequestedLogin.current = true;
      login().catch(() => {
        dispatch(loginError());
      });
    }, LOGIN_REDIRECT_DELAY);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isAuthenticated, login, dispatch]);

  useEffect(() => {
    if (loadingState === OidcUserStatus.Loading) {
      dispatch(loginStart());
    }
  }, [loadingState, dispatch]);

  useEffect(() => {
    if (loadingState === OidcUserStatus.LoadingError) {
      dispatch(loginError());
    }
  }, [loadingState, dispatch]);

  useEffect(() => {
    if (loadingState !== OidcUserStatus.Loaded) {
      return;
    }

    dispatch(loginSuccess(user));

    if (user && !user.userRole) {
      dispatch(setHasRequestedAccess(false));
    }
  }, [user, loadingState, dispatch]);
};
